import { useContext, useEffect, useState } from "react";
import { SecurityTask } from "../securityTasks/SecurityTasks.route";
import {
  Delete,
  Get,
  handleError,
  Patch,
  Post,
} from "../../../utils/fetch.util";
import { UserContext } from "../../../contexts/user.context";
import ConfirmationModal from "../../../components/modals/confirmationModal/confirmationModal.component";
import AddSecurityTaskModal from "../../../components/modals/AddSecurityTaskModal/AddSecurityTaskModal.component";
import { FiEdit2, FiSearch, FiTrash2 } from "react-icons/fi";
import NoData from "../../../components/noData/noData.component";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import { useTranslation } from "react-i18next";

export default function SecurityPlanTasks() {
  const { t } = useTranslation();
  const { getValidToken } = useContext(UserContext);
  const [tasks, setTasks] = useState<SecurityTask[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState<SecurityTask | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
  const [filters, setFilters] = useState<{
    search: string;
    security_post_id: string | null;
    date: string | null;
  }>({ search: "", security_post_id: null, date: null });
  const [securityPlans, setSecurityPlans] = useState<any[]>([]);

  const [pagination, setPagination] = useState<{
    page: number;
    pageItemsCount: number;
    count: number;
  }>({ page: 1, pageItemsCount: 10, count: 0 });

  const fetchTasks = async () => {
    const query: string[] = [];
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);
    if (filters.security_post_id)
      query.push(`security_post_id=${filters.security_post_id}`);
    if (filters.date) query.push(`date=${filters.date}`);
    const res = await Get(
      `security-post-task/get-all-tasks?${query.join("&")}`,
      getValidToken()
    );
    if (res?.error || !res?.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }
    setTasks(res.data);
    setPagination((prev) => ({ ...prev, count: res.count }));
    setIsLoading(false);
  };

  const getSecurityPlans = async () => {
    const query: string[] = [];
    query.push(`page=${pagination.page}`);
    query.push(`pageItemsCount=${pagination.pageItemsCount}`);
    if (filters.search.length > 0) query.push(`search=${filters.search}`);
    const res = await Get(
      `security-post/get-all-posts?${query.join("&")}`,
      getValidToken()
    );
    if (res?.error || !res?.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }
    setSecurityPlans(res.data);
    setPagination((p) => ({ ...p, count: res.count }));
    setIsLoading(false);
  };

  const handleAdd = async (task: SecurityTask) => {
    setIsLoading(true);
    const res = await Post(
      "security-post-task/create-task",
      task,
      getValidToken()
    );
    if (res?.error) handleError(res);
    setShowModal(false);
    fetchTasks();
    setIsLoading(false);
  };

  const handleEdit = async (id: number, task: SecurityTask) => {
    setIsLoading(true);
    const res = await Patch(
      `security-post-task/update-task?security_post_task_id=${id}`,
      task,
      getValidToken()
    );
    if (res?.error) handleError(res);
    setEditTask(null);
    fetchTasks();
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    setIsLoading(true);
    const res = await Delete(
      `security-post-task/delete-task?security_post_task_id=${taskToDelete}`,
      getValidToken()
    );
    if (res?.error) handleError(res);
    setTaskToDelete(null);
    fetchTasks();
    setIsLoading(false);
  };

  useEffect(() => {
    getSecurityPlans();
    fetchTasks();
    // eslint-disable-next-line
  }, [pagination.page, filters]);

  return (
    <>
      <div className="container bg-white p-6 px-8">
        <div className="flex items-center gap-3 mt-2 mb-6">
          <BackButton />
          <h1 className="text-5xl">{t("security_tasks")}</h1>
        </div>
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => setShowModal(true)}
            className="main_btn me-auto"
          >
            {/* <FiPlus className="inline mr-2" /> */}
            {t("add_task")}
          </button>
          <div className="flex gap-3">
            <div className="relative w-44">
              <select
                className="input w-full rounded-full p-3"
                value={filters.security_post_id || ""}
                onChange={(e) => {
                  const value = e.target.value || null;
                  setFilters({ ...filters, security_post_id: value });
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              >
                <option value="">{t("all_security_posts")}</option>
                {securityPlans.map((post) => (
                  <option key={post.id} value={post.id}>
                    {post.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative w-44">
              <input
                type="datetime-local"
                className="input w-full rounded-full p-3"
                value={filters.date ? new Date(filters.date).toISOString().slice(0, 16) : ""}
                onChange={(e) => {
                  let value = null;
                  if (e.target.value) {
                    const dateObj = new Date(e.target.value);
                    value = dateObj.toISOString();
                  }
                  setFilters({ ...filters, date: value });
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
              />
            </div>
            <div className="relative w-44">
              <input
                type="text"
                placeholder={t("search")}
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="input w-full rounded-full p-3 pr-10"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setFilters({
                      ...filters,
                      search: (e.target as HTMLInputElement).value,
                    });
                    setPagination((p) => ({ ...p, page: 1 }));
                  }
                }}
              />
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                }}
                onClick={() => {
                  setFilters({ ...filters, search: filters.search });
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                aria-label="Search"
                tabIndex={-1}
                type="button"
              >
                <FiSearch size={20} />
              </button>
            </div>
          </div>
        </div>
        {tasks.length === 0 ? (
          <NoData message={t("no_tasks_found")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-fixed rounded-md border-collapse w-full ">
              <thead>
                <tr>
                  <th className="border-2 p-4 py-2">{t("name")}</th>
                  <th className="border-2 p-4 py-2">{t("description")}</th>
                  <th className="border-2 p-4 py-2">{t("start_time")}</th>
                  <th className="border-2 p-4 py-2">{t("end_time")}</th>
                  <th className="border-2 p-4 py-2">{t("task_type")}</th>
                  {/* <th className="border-2 p-4 py-2">{t("logs")}</th>
                  <th className="border-2 p-4 py-2">{t("answers")}</th> */}
                  <th className="border-2 p-4 py-2">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className="border-2 p-4 py-2">{task.name}</td>
                    <td className="border-2 p-4 py-2">{task.description}</td>
                    <td className="border-2 p-4 py-2">{task.start_time}</td>
                    <td className="border-2 p-4 py-2">{task.end_time}</td>
                    <td className="border-2 p-4 py-2">{task.task_type}</td>
                    <td className="border-2 p-4 py-2">
                      <div className="flex gap-2">
                        <button
                          className="icon_btn"
                          onClick={() => setEditTask(task)}
                        >
                          <FiEdit2 size={20} />
                        </button>
                        <button
                          className="icon_btn"
                          onClick={() => setTaskToDelete(task.id!)}
                        >
                          <FiTrash2 size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AddSecurityTaskModal
        isShown={showModal || !!editTask}
        closeModal={() => {
          setShowModal(false);
          setEditTask(null);
        }}
        onSubmit={async (data) => {
          if (editTask && editTask.id !== undefined) {
            await handleEdit(editTask.id, data as any);
          } else {
            await handleAdd(data as any);
          }
        }}
        isLoading={isLoading}
        initialData={editTask}
      />
      <ConfirmationModal
        isShown={!!taskToDelete}
        closeConfirmationModal={() => setTaskToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isLoading}
        message={t("are_you_sure_delete")}
        title={t("confirmation")}
      />
    </>
  );
}
